import {
  MdCheckCircle,
  MdWarning,
  MdError,
  MdInfo,
  MdLightbulb,
  MdSchedule,
} from "react-icons/md";
import { SiNginx, SiPostgresql, SiSpringboot } from "react-icons/si";
import { IoLogoNodejs } from "react-icons/io";
import { FaServer } from "react-icons/fa";
import { useEffect, useState } from "react";
import { toLocalDate, formatLocalHour } from "../../../utils/dateUtils";

const ServicesInfo = ({
  servicesData,
  startDateTime,
  endDateTime,
  baseDate,
}) => {
  const [hourlyData, setHourlyData] = useState([]);

  useEffect(() => {
    if (!servicesData || servicesData.length === 0) {
      setHourlyData([]);
      return;
    }

    let dataArray = servicesData;
    if (!Array.isArray(servicesData)) {
      dataArray = [servicesData];
    }

    let filtered = dataArray;

    if (startDateTime && endDateTime) {
      const start = toLocalDate(startDateTime);
      const end = toLocalDate(endDateTime);

      filtered = dataArray.filter((item) => {
        const itemDate = toLocalDate(item.stoppedAt || item.createdAt);
        return itemDate >= start && itemDate <= end;
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      const dateA = toLocalDate(a.stoppedAt || a.createdAt);
      const dateB = toLocalDate(b.stoppedAt || b.createdAt);
      return dateB - dateA;
    });

    setHourlyData(sorted);
  }, [servicesData, startDateTime, endDateTime]);

  if (!servicesData || servicesData.length === 0 || hourlyData.length === 0) {
    return (
      <div className="p-4 sm:p-6 md:p-8 pt-0">
        <div className="bg-gray-800/30 rounded-xl border border-gray-700 p-8 text-center">
          <p className="text-gray-400">
            No service data available for selected time range
          </p>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    if (status === "OK") {
      return <MdCheckCircle className="text-green-500 text-base sm:text-lg" />;
    } else if (status === "WARNING") {
      return <MdWarning className="text-yellow-500 text-base sm:text-lg" />;
    } else {
      return <MdError className="text-red-500 text-base sm:text-lg" />;
    }
  };

  const getStatusColor = (status) => {
    if (status === "OK") return "text-green-500";
    if (status === "WARNING") return "text-yellow-500";
    return "text-red-500";
  };

  const getServiceIcon = (serviceName) => {
    switch (serviceName) {
      case "nginx":
        return <SiNginx className="text-primary-cyan text-lg sm:text-xl" />;
      case "pm2":
        return <IoLogoNodejs className="text-primary-cyan text-lg sm:text-xl" />;
      case "syslog":
        return <FaServer className="text-primary-cyan text-lg sm:text-xl" />;
      case "postgresql":
        return <SiPostgresql className="text-primary-cyan text-lg sm:text-xl" />;
      case "spring_boot":
        return <SiSpringboot className="text-primary-cyan text-lg sm:text-xl" />;
      default:
        return <FaServer className="text-primary-cyan text-lg sm:text-xl" />;
    }
  };

  const getServiceDisplayName = (serviceName) => {
    switch (serviceName) {
      case "nginx":
        return "Nginx";
      case "pm2":
        return "PM2";
      case "syslog":
        return "Syslog";
      case "postgresql":
        return "PostgreSQL";
      case "spring_boot":
        return "Spring Boot";
      default:
        return serviceName;
    }
  };

  const services = ["nginx", "pm2", "syslog", "postgresql", "spring_boot"];

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-0">
      <div className="space-y-6">
        {hourlyData.map((hourEntry, index) => (
          <div
            key={index}
            className="bg-gray-800/30 rounded-xl border border-gray-700 overflow-hidden"
          >
            <div className="bg-gray-800/50 p-4 sm:p-5 border-b border-gray-700">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <MdSchedule className="text-primary-cyan text-lg sm:text-xl" />
                  <h3 className="text-white font-semibold text-base sm:text-lg">
                    {formatLocalHour(hourEntry.stoppedAt || hourEntry.createdAt)}
                  </h3>
                </div>
                <div className="flex items-center gap-2 bg-primary-cyan/20 px-3 py-1.5 rounded-lg border border-primary-cyan/30 self-start">
                  {getStatusIcon(hourEntry.severity)}
                  <span className={`font-semibold text-sm ${getStatusColor(hourEntry.severity)}`}>
                    {hourEntry.severity || "UNKNOWN"}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((serviceName) => {
                  const serviceData = hourEntry.components?.[serviceName];
                  if (!serviceData) return null;

                  return (
                    <div
                      key={serviceName}
                      className="bg-gray-800/50 rounded-xl border border-gray-700 hover:border-primary-cyan/30 transition-all duration-300 overflow-hidden flex flex-col h-full"
                    >
                      <div className="p-4 border-b border-gray-700">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {getServiceIcon(serviceName)}
                            <h4 className="text-white font-semibold text-sm sm:text-base truncate">
                              {getServiceDisplayName(serviceName)}
                            </h4>
                          </div>
                          <div className="flex items-center gap-1.5 bg-primary-cyan/20 px-2 py-1 rounded-lg border border-primary-cyan/30 flex-shrink-0">
                            {getStatusIcon(serviceData.status)}
                            <span className={`font-semibold text-xs sm:text-sm ${getStatusColor(serviceData.status)}`}>
                              {serviceData.status || "UNKNOWN"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 flex-1 space-y-3">
                        <div className="flex items-start gap-2">
                          <MdInfo className="text-primary-cyan text-sm sm:text-base mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h5 className="text-gray-400 text-xs font-medium mb-1">Details</h5>
                            <p className="text-gray-300 text-xs sm:text-sm break-words leading-relaxed">
                              {serviceData.details || "No details available"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <MdLightbulb className="text-primary-cyan text-sm sm:text-base mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h5 className="text-gray-400 text-xs font-medium mb-1">Suggestion</h5>
                            <p className="text-primary-cyan font-medium text-xs sm:text-sm break-words leading-relaxed">
                              {serviceData.suggestion || "No suggestion available"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesInfo;